import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Banknote, CalendarCheck, FileSignature, Truck, Settings, AlertCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/translations';
import { cn } from '@/lib/utils';

const paymentIcons = [FileSignature, Truck, Settings];

export const PaymentSection = ({ formData, setFormData, t, language }) => {
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  const opt = t.paymentPlanOption;
  const pp = t.paymentPlan;
  const totalPrice = formData.productDetails.totalPrice || 0;

  const handleOptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      usePaymentPlan: value === 'yes'
    }));
  };

  const currentValue = formData.usePaymentPlan ? 'yes' : 'no';

  const paymentAmounts = [
    totalPrice * 0.5,  // 50%
    totalPrice * 0.25, // 25%
    totalPrice * 0.25  // 25%
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Payment Option Selection */}
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <CreditCard className="w-5 h-5 text-primary" />
            {opt.title}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">{opt.description}</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <RadioGroup 
            value={currentValue}
            onValueChange={handleOptionChange}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {/* Yes - Payment Plan Option */}
            <Label 
              htmlFor="paymentYes" 
              className={cn(
                "cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 hover:border-primary/50",
                currentValue === 'yes' 
                  ? "border-primary bg-primary/5 shadow-glow" 
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-start gap-4">
                <RadioGroupItem 
                  value="yes" 
                  id="paymentYes" 
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarCheck className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-foreground">{opt.yes}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {opt.yesDescription}
                  </p>
                  
                  {/* Payment breakdown preview */}
                  <div className="space-y-2 p-3 rounded-lg bg-muted/50">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">1. {language === 'de' ? 'Zahlung (50%)' : 'Payment (50%)'}</span>
                      <span className="font-medium">{formatCurrency(totalPrice * 0.5, locale)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">2. {language === 'de' ? 'Zahlung (25%)' : 'Payment (25%)'}</span>
                      <span className="font-medium">{formatCurrency(totalPrice * 0.25, locale)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">3. {language === 'de' ? 'Zahlung (25%)' : 'Payment (25%)'}</span>
                      <span className="font-medium">{formatCurrency(totalPrice * 0.25, locale)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Label>

            {/* No - Full Payment Option */}
            <Label 
              htmlFor="paymentNo" 
              className={cn(
                "cursor-pointer rounded-xl border-2 p-6 transition-all duration-300 hover:border-primary/50",
                currentValue === 'no' 
                  ? "border-primary bg-primary/5 shadow-glow" 
                  : "border-border bg-card"
              )}
            >
              <div className="flex items-start gap-4">
                <RadioGroupItem 
                  value="no" 
                  id="paymentNo" 
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Banknote className="w-5 h-5 text-success" />
                    <span className="font-semibold text-foreground">{opt.no}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {opt.noDescription}
                  </p>
                  
                  {/* Full payment preview */}
                  <div className="p-3 rounded-lg bg-success/10 border border-success/20">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {language === 'de' ? 'Gesamtbetrag' : 'Total Amount'}
                      </span>
                      <span className="text-lg font-bold text-success">
                        {formatCurrency(totalPrice, locale)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Label>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Conditionally show Payment Plan Details */}
      {formData.usePaymentPlan && (
        <>
          <Card className="border-0 shadow-lg bg-gradient-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-xl text-foreground">
                <CalendarCheck className="w-5 h-5 text-primary" />
                {pp.title}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">{pp.description}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Timeline */}
              <div className="relative">
                {pp.payments.map((payment, index) => {
                  const Icon = paymentIcons[index];
                  const amount = paymentAmounts[index];
                  
                  return (
                    <div key={index} className="relative">
                      {/* Connector line */}
                      {index < pp.payments.length - 1 && (
                        <div className="absolute left-5 top-12 w-0.5 h-full bg-border" />
                      )}
                      
                      <div className="flex gap-4 pb-6">
                        {/* Icon */}
                        <div className="relative z-10 flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pt-0.5">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h4 className="font-semibold text-foreground">{payment.title}</h4>
                            <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                              {payment.percentage}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {payment.description}
                          </p>
                          <div className="inline-block px-3 py-1.5 rounded-lg bg-muted">
                            <span className="text-sm font-medium text-foreground">
                              {formatCurrency(amount, locale)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Separator className="my-6" />

              {/* Payment Summary */}
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <h4 className="font-semibold text-foreground mb-3">
                  {language === 'de' ? 'Zahlungsübersicht' : 'Payment Summary'}
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-card">
                    <p className="text-xs text-muted-foreground mb-1">
                      {language === 'de' ? 'Bei Vertragsunterzeichnung' : 'At Contract Signing'}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(paymentAmounts[0], locale)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-card">
                    <p className="text-xs text-muted-foreground mb-1">
                      {language === 'de' ? 'Bei Lieferung' : 'Upon Delivery'}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(paymentAmounts[1], locale)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-card">
                    <p className="text-xs text-muted-foreground mb-1">
                      {language === 'de' ? 'Bei Installation' : 'Upon Installation'}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(paymentAmounts[2], locale)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes Card */}
          <Card className="border-0 shadow-md bg-muted/30">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground mb-2">{pp.notes.title}</h4>
                  <ul className="space-y-2">
                    {pp.notes.items.map((note, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-primary">•</span>
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Full Payment Summary (when not using payment plan) */}
      {!formData.usePaymentPlan && (
        <Card className="border-0 shadow-lg bg-gradient-card">
          <CardContent className="pt-6">
            <div className="p-6 rounded-lg bg-success/5 border border-success/20">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-success" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-1">
                    {language === 'de' ? 'Vollzahlung bei Vertragsunterzeichnung' : 'Full Payment at Contract Signing'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'de' 
                      ? 'Der gesamte Betrag ist bei Unterzeichnung des Vertrags fällig.'
                      : 'The full amount is due upon signing the contract.'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-success">
                    {formatCurrency(totalPrice, locale)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'de' ? 'inkl. MwSt.' : 'incl. VAT'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentSection;
